import { useState, useEffect } from 'react'
import isPropValid from '@emotion/is-prop-valid'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useNavigate, useLocation, Outlet, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '../../../store'
import { setEventDetailsStep, setEventDetailsId, getEventDetails } from '../../../store.slice'
import { ContextProps } from './CreateEvents.Context'

const Container = styled(Stack)(({ theme }) => ({
  height: 'calc(100% - 1rem)',
  width: 'calc(100% - 1rem)',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 10,
  boxShadow: theme.boxShadows.main,
  flexDirection: 'column',
  overflow: 'hidden',
}))

const IconStep = styled(Box)(({ theme }) => ({
  backgroundColor: theme.colors.whiteBackground,
  color: theme.palette.text.primary,
  borderRadius: '0.5rem',
  border: `0.15rem solid ${theme.colors.secondary}`,
  width: '2.9rem',
  height: '2.9rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: theme.typography.h3.fontSize,
  fontWeight: theme.typography.h3.fontWeight,
}))
const StepButton = styled(Button, {
  shouldForwardProp: prop => typeof prop === 'string' && isPropValid(prop) && prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  height: '4rem',
  gap: '0.5rem',
  justifyContent: 'flex-start',
  textTransform: 'none',
  color: active ? theme.colors.accent[200] : theme.palette.text.primary,
  backgroundColor: active ? theme.colors.whiteBackground : theme.colors.secondaryBackground,
  borderColor: active ? theme.colors.primary : theme.colors.secondary,

  ':disabled': {
    backgroundColor: theme.colors.greyBackground,
    borderColor: theme.colors.disabled,
    opacity: 0.5,
  },

  '& > div': {
    color: active ? theme.colors.accent[200] : theme.palette.text.primary,
    borderColor: active ? theme.colors.primary : theme.colors.secondary,
    backgroundColor: active ? theme.colors.secondaryBackground : theme.colors.whiteBackground,
  },

  '&:hover > div': {
    backgroundColor: theme.colors.secondaryBackground,
    borderColor: theme.colors.accent[200],
    color: theme.colors.accent[200],
  },
}))

export default function CreateEventLayout() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [step, setStep] = useState<ContextProps['step']>(1)

  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const eventDetails = useAppSelector(state => getEventDetails(state)(eventId))

  useEffect(() => {
    setStep(eventDetails.step)

    if (eventId === 'new') {
      switch (eventDetails.step) {
        case 2:
          navigate(`/admin/events/${eventId}/sessions`, { replace: true })
          break
        case 3:
          navigate(`/admin/events/${eventId}/logistic`, { replace: true })
          break
        case 4:
          navigate(`/admin/events/${eventId}/conditions`, { replace: true })
          break
        case 5:
          navigate(`/admin/events/${eventId}/validation`, { replace: true })
          break
        case 1:
        default:
          navigate(`/admin/events/${eventId}/event`, { replace: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(setEventDetailsStep({ data: step, key: eventId }))
  }, [step, eventId, dispatch])

  useEffect(() => {
    dispatch(setEventDetailsId({ data: eventId, key: eventId }))
  }, [dispatch, eventId])

  return (
    <Container>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flex="1 0" height="1px">
        <Stack
          direction={{ xs: 'row', md: 'column' }}
          spacing={1}
          minWidth="300px"
          padding="1rem"
          sx={theme => ({
            backgroundColor: theme.colors.secondaryBackground,
            overflowX: { xs: 'auto', md: 'hidden' },
          })}
        >
          <StepButton
            onClick={() => {
              navigate(`/admin/events/${eventId}/event`)
            }}
            active={location.pathname === `/admin/events/${eventId}/event`}
            sx={{ minWidth: { xs: '250px', md: 'auto' } }}
          >
            <IconStep>1</IconStep>
            <h3>{t("Détails de l'événement")}</h3>
          </StepButton>
          <StepButton
            onClick={() => {
              navigate(`/admin/events/${eventId}/sessions`)
            }}
            disabled={step < 2}
            active={location.pathname === `/admin/events/${eventId}/sessions`}
            sx={{ minWidth: { xs: '250px', md: 'auto' } }}
          >
            <IconStep>2</IconStep>
            <h3>{t('Séances et interventions')}</h3>
          </StepButton>
          <StepButton
            onClick={() => {
              navigate(`/admin/events/${eventId}/logistic`)
            }}
            disabled={step < 3}
            active={location.pathname === `/admin/events/${eventId}/logistic`}
            sx={{ minWidth: { xs: '250px', md: 'auto' } }}
          >
            <IconStep>3</IconStep>
            <h3>{t('Logistique')}</h3>
          </StepButton>
          <StepButton
            onClick={() => {
              navigate(`/admin/events/${eventId}/conditions`)
            }}
            disabled={step < 4}
            active={location.pathname === `/admin/events/${eventId}/conditions`}
            sx={{ minWidth: { xs: '250px', md: 'auto' } }}
          >
            <IconStep>4</IconStep>
            <h3>{t("Modalités d'inscription")}</h3>
          </StepButton>
          <StepButton
            onClick={() => {
              navigate(`/admin/events/${eventId}/validation`)
            }}
            active={location.pathname === `/admin/events/${eventId}/validation`}
            disabled={step < 5}
            sx={{ minWidth: { xs: '250px', md: 'auto' } }}
          >
            <IconStep>5</IconStep>
            <h3>{t('Résumé et validation')}</h3>
          </StepButton>
        </Stack>

        <Outlet
          context={{
            step,
            setStep,
          }}
        />
      </Stack>
    </Container>
  )
}
